import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useNotification } from '../../../components/ui/NotificationProvider';

const QuickUploadWidget = ({ onFileUpload }) => {
  const { showWarning } = useNotification();
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e?.dataTransfer?.files);
    handleFileUpload(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files);
    handleFileUpload(files);
  };

  const handleFileUpload = async (files) => {
    if (files?.length === 0) return;

    const validFiles = files?.filter(file => 
      file?.type?.includes('sheet') || 
      file?.type?.includes('csv') || 
      file?.name?.endsWith('.xlsx') || 
      file?.name?.endsWith('.xls') || 
      file?.name?.endsWith('.csv')
    );

    if (validFiles?.length === 0) {
      showWarning?.('Por favor selecciona archivos Excel (.xlsx, .xls) o CSV válidos', { title: 'Formato no soportado' });
      return;
    }

    setIsProcessing(true);
    
    // Simulate file processing
    setTimeout(() => {
      setIsProcessing(false);
      if (onFileUpload) {
        onFileUpload(validFiles);
      }
      navigate('/bulk-upload');
    }, 1500);
  };

  const downloadTemplate = () => {
    const csvContent = `title,price,category,description,condition,stock,images
"Smartphone Samsung Galaxy A54 128GB","299.99","Celulares y Teléfonos","Smartphone con pantalla AMOLED de 6.4 pulgadas","new","25","https://example.com/image1.jpg"
"Auriculares Bluetooth Sony WH-1000XM4","199.50","Audio","Auriculares inalámbricos con cancelación de ruido","new","15","https://example.com/image2.jpg"
"Laptop HP Pavilion 15.6 Intel i5","549.00","Computación","Laptop con procesador Intel Core i5 y 8GB RAM","new","8","https://example.com/image3.jpg"`;
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plantilla_productos_mercadolibre.csv';
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  return (
    <div className="bg-surface border border-border rounded-lg p-6 shadow-soft">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-text-primary">Carga de Productos</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={downloadTemplate}
          iconName="Download"
          iconPosition="left"
        >
          Plantilla
        </Button>
      </div>
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
          isDragOver 
            ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/30'
        } ${isProcessing ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !isProcessing && fileInputRef?.current?.click()}
      >
        {isProcessing ? (
          <div className="space-y-3">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="font-medium text-text-primary">Procesando archivos...</p>
            <p className="text-sm text-text-secondary">Preparando para carga masiva</p>
          </div>
        ) : (
          <div className="space-y-3">
            <Icon 
              name="Upload" 
              size={48} 
              className={`mx-auto ${isDragOver ? 'text-primary' : 'text-text-secondary'}`}
            />
            <div>
              <p className="font-medium text-text-primary mb-1">
                {isDragOver ? 'Suelta los archivos aquí' : 'Arrastra archivos Excel o CSV'}
              </p>
              <p className="text-sm text-text-secondary">
                o <span className="text-primary font-medium">haz clic para seleccionar</span>
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-2 text-xs text-text-secondary">
              <span className="px-2 py-1 bg-muted rounded">.xlsx</span>
              <span className="px-2 py-1 bg-muted rounded">.xls</span>
              <span className="px-2 py-1 bg-muted rounded">.csv</span>
            </div>
          </div>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".csv,.xlsx,.xls"
        onChange={handleFileSelect}
        className="hidden"
      />
      <div className="mt-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/upload-history')}
          iconName="History"
          iconPosition="left"
          fullWidth
        >
          Ver Historial de Cargas
        </Button>
      </div>
    </div>
  );
};

export default QuickUploadWidget;